import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, GripVertical, Package, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

function Row({ p, index }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`catprod-row-${p.sku || p.id}`} className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-slate-950">
      <button {...attributes} {...listeners} data-testid={`catprod-drag-${p.sku || p.id}`} className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none"><GripVertical className="h-4 w-4" /></button>
      <span className="w-6 text-xs font-semibold text-slate-400 tabular-nums">{index + 1}</span>
      <div className="h-10 w-10 shrink-0 rounded-md bg-slate-50 dark:bg-slate-800 overflow-hidden grid place-items-center">
        {p.image_url ? <img src={resolveUrl(p.image_url)} alt="" className="h-full w-full object-contain" /> : <Package className="h-4 w-4 text-slate-300" />}
      </div>
      <span className="flex-1 min-w-0 truncate text-sm font-medium text-slate-800 dark:text-slate-200">{p.name}</span>
      {p.sku && <span className="text-xs text-slate-400">{p.sku}</span>}
    </div>
  );
}

export default function CategoryProducts() {
  const { name } = useParams();
  const navigate = useNavigate();
  const category = decodeURIComponent(name || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    api.get("/products").then((r) => {
      const list = r.data
        .filter((p) => !p.archived && !p.deleted && p.category === category)
        .sort((a, b) => (a.sort_order ?? 999999) - (b.sort_order ?? 999999) || (a.name || "").localeCompare(b.name || ""));
      setItems(list);
    }).finally(() => setLoading(false));
  }, [category]);

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(items, items.findIndex((p) => p.id === active.id), items.findIndex((p) => p.id === over.id));
    setItems(next);
    setSaving(true);
    api.put("/products/reorder", { category, ids: next.map((p) => p.id) })
      .then(() => toast.success("Order saved — sets the kiosk default for this category"))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setSaving(false));
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <button data-testid="catprod-back" onClick={() => navigate("/admin/categories")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-2"><ArrowLeft className="h-4 w-4" /> Categories</button>
        <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">{category}</h1>
        <p className="text-slate-500 mt-1">Drag to set the default product order for this category on kiosks. {saving && <span className="text-indigo-600"><Loader2 className="h-3.5 w-3.5 inline animate-spin" /> saving…</span>}</p>
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Note: if a kiosk has a planogram, its layout order takes priority over this default.</p>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800 max-w-3xl">
        {loading && <p className="text-center text-slate-400 py-8"><Loader2 className="h-5 w-5 animate-spin inline" /></p>}
        {!loading && !items.length && <p className="text-center text-slate-400 py-8">No active products in this category.</p>}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((p) => p.id)} strategy={verticalListSortingStrategy}>
            {items.map((p, i) => <Row key={p.id} p={p} index={i} />)}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}
